import React from 'react';
import {
  Box,
  IconButton,
  TextField,
  Slider,
  TextFieldProps,
} from '@mui/material';
import {t} from 'i18next';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export interface PercentageSliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

const PercentageSlider: React.FC<PercentageSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 5,
}) => {
  const handleInputChange: TextFieldProps['onChange'] = (e) => {
    const raw = e.target.value.replace('%', '');
    let val = parseInt(raw, 10);

    if (isNaN(val)) val = min;
    if (val > max) val = max;
    if (val < min) val = min;

    onChange(val);
  };

  const increment = () => onChange(Math.min(max, value + step));
  const decrement = () => onChange(Math.max(min, value - step));

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      width="100%"
      flexDirection={{xs: 'column', md: 'row'}}
    >
      {/* Input Box */}
      <Box display="flex" alignItems="center" className="cohesion-input">
        <TextField
          className="cohesion-input-text"
          variant="standard"
          value={`${value}%`}
          onChange={handleInputChange}
          slotProps={{input: {'data-testid': 'cohesion-input-field'}}}
          InputProps={{
            disableUnderline: true,
          }}
        />

        {/* Increment / Decrement Buttons */}
        <Box display="flex" flexDirection="column">
          <IconButton
            size="small"
            onClick={increment}
            className="slider-button"
            data-testid="percentage-slider-increase-icon-button"
            aria-label= {t('corpus.labelCohesionIncrement')}
          >
            <ArrowDropUpIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={decrement}
            className="slider-button"
            data-testid="percentage-slider-decrease-icon-button"
            aria-label= {t('corpus.labelCohesionDecrement')}
          >
            <ArrowDropDownIcon />
          </IconButton>
        </Box>
      </Box>
      {/* Slider */}
      <Slider
        value={value}
        onChange={(_, val) => onChange(val as number)}
        min={min}
        max={max}
        step={step}
        aria-label="cohesion-percentage-slider"
        sx={{
          color: '#8C9EBA',
          height: 3,
          '& .MuiSlider-thumb': {
            backgroundColor: '#5265FF',
          },
        }}
      />
    </Box>
  );
};

export default PercentageSlider;
